import {
  AutocompleteInputChangeReason,
  Box,
  CircularProgress,
  createFilterOptions,
  InputLabel,
  TextField,
  Typography,
} from "@mui/material";
import React, { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { LabeledAutoComplete } from "../../UI/LabeledAutoComplete";
import { GridSortDirection } from "@mui/x-data-grid";
import SupplierService from "../../../services/supplier.service";
import {
  isTemporarySupplierOption,
  resolveSelectedSupplierId,
} from "../../../utils/budget-supplier-id";
import {
  useBudgetMeta,
  useBudgetProvider,
  useBudgetSuppliersState,
} from "../../../contexts/BudgetContext/projectbudgets.context";

const INITIAL_STATE = {
  page: 0,
  pageSize: 20,
  order: "asc" as GridSortDirection,
  sortField: "name",
  search: "",
  origin: "",
};

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedValue(value);
    }, delayMs);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [value, delayMs]);

  return debouncedValue;
}

type PropTypes = {
  /** Devolver `false` recusa a escolha: o campo volta ao fornecedor atual. */
  onSelectSupplier(value: string, field: string): void | boolean;
  provider_id: string;
  title: string;
  disabled?: boolean;
  isBudget?: boolean;
  error?: boolean;
  /**
   * Recebe a opção completa selecionada (ou `null` ao limpar), além do
   * `onSelectSupplier` habitual. Usado por quem precisa do nome/CNPJ do
   * fornecedor e resolve o id interno por conta própria — caso da
   * transferência de categoria, que só materializa o id no confirm.
   */
  onSelectSupplierOption?: (option: any | null) => void;
  /** Encaminhado ao Autocomplete (ex.: bloquear o fornecedor atual). */
  getOptionDisabled?: (option: any) => boolean;
  /** Sobrescreve o rótulo padrão "Fornecedor". */
  label?: string;
  /**
   * Devolve o `_id` do fornecedor sem o sufixo `|dup|N`.
   *
   * O padrão existe para a visão "Por Fornecedor", onde cada linha é UM
   * fornecedor: escolher ali um que já está na lista significa criar uma
   * segunda linha, e essa linha precisa de id próprio.
   *
   * No formulário do agrupamento a semântica é a oposta — escolher um
   * fornecedor que já existe significa REUSAR a linha dele. Sufixar ali criava
   * o fornecedor duplicado que o QA relatou ("ele sai criando aqui dois").
   *
   * Não afeta o fornecedor temporário ("A DEFINIR"), que continua ganhando
   * `-<forma>-<n>`: ali cada linha é mesmo um espaço reservado diferente.
   */
  reuseExistingSupplier?: boolean;
};

const SupplierAutoComplete: React.FC<PropTypes> = ({
  onSelectSupplier,
  provider_id,
  disabled,
  isBudget,
  error,
  onSelectSupplierOption,
  getOptionDisabled,
  label,
  reuseExistingSupplier,
}: PropTypes) => {
  const [inputSupplierValue, setInputSupplerValue] = useState("");
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const debouncedInputSupplierValue = useDebouncedValue(
    inputSupplierValue,
    300
  );
  // Subscribe to the slim slices the autocomplete actually reads. Reading the
  // full `useBudget()` here meant every keystroke in any service input
  // bumped this hook (and its useQuery) for every supplier row in the tree.
  const { activeCalcOption } = useBudgetMeta();
  const { supplierList } = useBudgetSuppliersState();

  // Resolved provider lookup — served from the single `useQueries` lifted
  // into `BudgetProvider`. We keep `useQuery` below for the search box,
  // but the row-level "who is this provider id?" hook is now zero-cost
  // (a Map read), eliminating the second per-row provider request that
  // used to live alongside the one in BudgetSupplier.
  const provider = useBudgetProvider(provider_id);
  const [supplierAutoCompleteSearch, setSupplierAutoCompleteSearch] =
    useState<any>(INITIAL_STATE);
  const { isLoading, data } = useQuery({
    queryKey: [
      "providers_search",
      supplierAutoCompleteSearch,
      debouncedInputSupplierValue,
    ],
    queryFn: () =>
      SupplierService.search({
        ...supplierAutoCompleteSearch,
        search: debouncedInputSupplierValue,
      }),
    enabled: isAutocompleteOpen,
    staleTime: 1000 * 30,
    cacheTime: 1000 * 60,
  });

  const [supplierValue, setSupplierValue] = useState<any>(null);
  function selectSupplier(newValue: any, id: string) {
    setSupplierValue(newValue);
    const accepted = onSelectSupplier(
      id,
      isBudget ? newValue?.name : "provider_id"
    );
    if (accepted === false) {
      // Troca recusada (ex.: MMV-2626): o `provider` não muda, então o efeito
      // de sincronização abaixo não roda — volta o campo aqui.
      setSupplierValue(provider?._id ? provider : null);
      setInputSupplerValue(provider?._id ? provider.name : "");
    }
  }
  function supplierChangeHandler(
    e: SyntheticEvent<Element, Event>,
    newValue: any | null
  ) {
    onSelectSupplierOption?.(newValue ?? null);
    if (!!isBudget && newValue?._id) {
      // A regra do sufixo mora em `budget-supplier-id.ts` — era a quarta cópia
      // dela neste repositório, e cópias divergentes fazem duas linhas
      // disputarem o mesmo id.
      selectSupplier(
        newValue,
        resolveSelectedSupplierId({
          realSupplierId: newValue._id,
          calcOption: activeCalcOption,
          supplierList,
          currentSupplierId: provider_id,
          isTemporary: isTemporarySupplierOption(newValue),
          reuseExisting: reuseExistingSupplier,
        })
      );
      return;
    }
    selectSupplier(newValue, newValue?._id || "");
  }
  function supplierInputChangeHandler(
    e: SyntheticEvent<Element, Event>,
    newValue: string,
    reason: AutocompleteInputChangeReason
  ) {
    // #MMV-2527: "reset" é o MUI preenchendo o input com
    // getOptionLabel(selecionado) (a razão social | nome fantasia | CNPJ
    // inteira) assim que uma opção é escolhida — não é o usuário buscando.
    // Sem esse guard, reabrir o campo mostrava esse texto cru selecionado
    // e a busca (usando ele como termo) não batia com nada ("No options").
    if (reason === "reset") return;
    setInputSupplerValue(newValue);
  }

  useEffect(() => {
    if (!provider_id) {
      setInputSupplerValue("");
      setSupplierValue(null);
      return;
    }
    if (provider && provider._id) {
      setSupplierValue(provider);
      setInputSupplerValue(provider.name);
    }
    // `provider_id` preenchido e lookup ainda em voo (ou falhou): não apaga
    // a seleção local. Escolher um fornecedor novo — ainda fora do mapa
    // compartilhado — esvaziava o campo no render seguinte. O usuário via o
    // preenchimento sumir, clicava em "Novo fornecedor" de novo e ganhava o
    // próximo número sequencial.
  }, [provider, provider_id]);

  const filterOptions = createFilterOptions({
    stringify: (option: any) => {
      const normalizeDigits = (value: any) =>
        String(value ?? "").replace(/[^\d]/g, "");

      return (
        option.name +
        option.nickname +
        option.tax_id +
        option.cnpj_cpf +
        normalizeDigits(option.tax_id) +
        normalizeDigits(option.cnpj_cpf)
      );
    },
  });

  const getOptionLabel = (option: any) => {
    const isTBD = option.cnpj_cpf?.includes("tbd");
    if (isTBD) return option?.name;
    return `${option.name} | ${option.nickname} | ${option.cnpj_cpf}` || "";
  };

  // #MMV-2527: mesma linha secundária (Nome Fantasia | CNPJ) usada tanto na
  // lista quanto no campo já selecionado, pra manter os dois em sincronia.
  const describeSecondaryLine = (option: any) =>
    [option.nickname, option.cnpj_cpf].filter(Boolean).join(" | ");

  const escapeRegExp = (value: string) =>
    value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // Destaca (negrito + azul) o trecho do texto pesquisado dentro do texto
  // exibido, igual à referência visual do card.
  const highlightMatch = (text: string, query: string): React.ReactNode => {
    const trimmedQuery = query.trim();
    if (!text || !trimmedQuery) return text;
    const regex = new RegExp(`(${escapeRegExp(trimmedQuery)})`, "gi");
    const parts = text.split(regex);
    if (parts.length === 1) return text;
    return parts.map((part, index) =>
      index % 2 === 1 ? (
        <Box
          key={index}
          component="span"
          sx={{ color: "#1361A4", fontWeight: 700 }}
        >
          {part}
        </Box>
      ) : (
        part
      )
    );
  };

  // Razão social em destaque na linha de cima, nome fantasia e CNPJ
  // separados por "|" na linha de baixo — mesmo padrão em toda listagem de
  // fornecedor (aberta pela primeira vez ou reaberta pra trocar seleção).
  // Trecho pesquisado sai realçado nas duas linhas.
  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: any
  ) => {
    const isTBD = option.cnpj_cpf?.includes("tbd");
    const secondaryLine = describeSecondaryLine(option);

    return (
      <Box
        component="li"
        {...props}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start !important",
          gap: "3px",
          py: "10px !important",
        }}
      >
        <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: "#1A1F27" }}>
          {highlightMatch(option.name, inputSupplierValue)}
        </Typography>
        {!isTBD && secondaryLine && (
          <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
            {highlightMatch(secondaryLine, inputSupplierValue)}
          </Typography>
        )}
      </Box>
    );
  };

  // #MMV-2527: com o campo fechado e um fornecedor selecionado, mostra o
  // mesmo padrão de 2 linhas da lista (razão social em negrito + nome
  // fantasia | CNPJ) em vez do texto corrido que o <input> nativo permitiria.
  // A camada de exibição fica por cima, com pointer-events desligado, pra
  // cliques/foco continuarem indo pro input de verdade por baixo.
  const isTBDSelected = supplierValue?.cnpj_cpf?.includes("tbd");
  const selectedSecondaryLine = supplierValue
    ? describeSecondaryLine(supplierValue)
    : "";
  const showRichSelectedDisplay =
    !isAutocompleteOpen &&
    !!supplierValue &&
    !isTBDSelected &&
    !!selectedSecondaryLine;

  // #MMV-2527: desabilitado nunca abre — em vez de um Autocomplete fingindo
  // ser texto estático (lutando contra chevron/contorno/-webkit-text-fill-color
  // do MUI), é só um campo de texto mesmo: mais simples e sem as disputas de
  // especificidade CSS que isso trazia.
  if (disabled) {
    return (
      <Box sx={{ width: "100%" }}>
        {(label ?? "Fornecedor") && (
          <InputLabel style={{ fontSize: "13px" }}>
            {label ?? "Fornecedor"}
          </InputLabel>
        )}
        <Box
          sx={{
            width: "100%",
            // Mesma altura do input "size=small" de sempre (40px) — só
            // porque agora tem 2 linhas não pode virar um select gigante.
            height: 40,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: "14px",
            borderRadius: "4px",
            border: "1px solid rgba(0, 0, 0, 0.23)",
            backgroundColor: "#F3F4F6",
          }}
        >
          {supplierValue ? (
            <>
              <Typography
                noWrap
                sx={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: "#6B7280",
                  lineHeight: 1.15,
                }}
              >
                {supplierValue.name}
              </Typography>
              {!isTBDSelected && selectedSecondaryLine && (
                <Typography
                  noWrap
                  sx={{ fontSize: 11, color: "#9CA3AF", lineHeight: 1.15 }}
                >
                  {selectedSecondaryLine}
                </Typography>
              )}
            </>
          ) : (
            <Typography noWrap sx={{ fontSize: 13, color: "#9CA3AF" }}>
              Selecione um fornecedor
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
      }}
    >
      <LabeledAutoComplete
        filterOptions={filterOptions}
        options={data?.providers || []}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        isOptionEqualToValue={(option: any, value: any) =>
          option?._id === value?._id
        }
        size="small"
        inputValue={inputSupplierValue}
        value={supplierValue}
        onInputChange={supplierInputChangeHandler}
        onChange={supplierChangeHandler}
        onOpen={() => {
          setIsAutocompleteOpen(true);
          // Reabrir com algo já selecionado: começa a lista limpa (ordem
          // alfabética completa) em vez de filtrar pelo texto cru que
          // sobrou da seleção anterior.
          if (supplierValue) setInputSupplerValue("");
        }}
        onClose={() => setIsAutocompleteOpen(false)}
        label={label ?? `Fornecedor`}
        getOptionDisabled={getOptionDisabled}
        renderInput={(params) => (
          // Wrapper relativo escopado só à caixa do input (não ao label
          // acima) — é o que faz a camada de exibição de baixo alinhar
          // certo com a borda em vez de flutuar a partir do topo do label.
          <Box sx={{ position: "relative", width: "100%" }}>
            <TextField
              placeholder="Selecione um fornecedor"
              {...params}
              fullWidth
              error={error}
              style={{ background: "#FFF" }}
              sx={
                showRichSelectedDisplay
                  ? {
                      "& .MuiOutlinedInput-root": {
                        minHeight: 46,
                        py: "3px !important",
                      },
                      "& .MuiAutocomplete-input": {
                        color: "transparent",
                        caretColor: "transparent",
                      },
                    }
                  : undefined
              }
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
            {showRichSelectedDisplay && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: "1px",
                  pl: "14px",
                  pr: "40px",
                  pointerEvents: "none",
                  overflow: "hidden",
                }}
              >
                <Typography
                  noWrap
                  sx={{
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: "#1A1F27",
                    lineHeight: 1.3,
                  }}
                >
                  {supplierValue.name}
                </Typography>
                <Typography
                  noWrap
                  sx={{ fontSize: 12, color: "#6B7280", lineHeight: 1.3 }}
                >
                  {selectedSecondaryLine}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      />
    </Box>
  );
};

export default SupplierAutoComplete;
